import React, { useMemo, useState } from 'react';
import qs from 'qs';
import _ from 'underscore';
import api from 'helpers/api';
import dateFormatter from 'helpers/date_formatter';
import { useAngularService } from 'helpers/get_angular_service';
import sources from 'helpers/sources';
import { useExtendState } from 'helpers/use_extend_state';
import { useFiltersOpen } from 'helpers/use_filters_open';
import Link from '../../../components/link';
import Table from '../../../components/table';
import ExportButton from '../../../components/export_button';
import { PageSettings } from 'react/types/page_settings';
import Input from '../../../components/input';
import ContactLink from '../../../components/contact_link';
import ToggleTableFiltersButton from '../../../components/toggle_table_filters_button';
import EnrolledSequencesReportFilters from './enrolled_sequences_report_filters';
import Container from '../../../components/container';
import DateInput from '../../../components/date_input';
import useOnMount from 'helpers/use_on_mount';
import usePermissions from 'helpers/use_permissions';
import FeatureNames from 'constants/feature_names';

interface Sorting {
  [key: string]: 'asc' | 'desc';
}

const EnrolledSequencesReport = () => {
  const pageId = 'enrolled_sequences_list';
  const GTMService = useAngularService('GTMService');
  const $rootScope = useAngularService('$rootScope');
  const $window = useAngularService('$window');
  const $location = useAngularService('$location');
  const PageSettingsService = useAngularService('PageSettingsService');
  const MasqueradingService = useAngularService('MasqueradingService');
  const AdminUserService = useAngularService('AdminUserService');
  const SequenceService = useAngularService('SequenceService');
  const ReactModal = useAngularService('ReactModal');

  const permissions = usePermissions();
  const [loading, setLoading] = useState(false);
  const [humanizedTypes, setHumanizedTypes] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, extendFilters, setFilters] = useExtendState<any>($location.search());
  const [sorting, setSorting] = useState<Sorting>(null);
  const [sequences, setEnrolledSequences] = useState([]);
  const [filtersOpen, toggleFilters] = useFiltersOpen('enrolledSequences', true);
  const isMasquerading = MasqueradingService.isMasquerading();

  const defaultListPageSettings = { page_size: 10 };
  const [pageSettings, extendPageSettings] = useExtendState<PageSettings>(defaultListPageSettings);
  const [pageSettingsLoaded, setPageSettingsLoaded] = useState(false);

  const getPageSettings = async () => {
    const settings = await PageSettingsService.getSettings(pageId);
    extendPageSettings(settings);
    if (settings.order_column_name && settings.order_direction) {
      setSorting({ [settings.order_column_name]: settings.order_direction });
    } else {
      setSorting({ enrolled: 'desc' });
    }
    setPageSettingsLoaded(true);
  };

  useOnMount(() => {
    GTMService.trackPage('Contact Report - Sequence Enrolments');
    getPageSettings();
    getTriggers();
    getCategories();
  });

  const getTriggers = () => {
    const url = sources.sequence($rootScope.gym.id, 'new');

    api.get(url).then((sequence) => {
      setHumanizedTypes(sequence.humanized_trigger_names);
      setTriggers(SequenceService.getTriggers(sequence));
    });
  };

  const getCategories = () => {
    const url = sources.sequenceCategories($rootScope.gym.id);

    api.get(url, { count: 1000 }).then((response) => {
      setCategories(response);
    });
  };

  const setPageSize = (count: string | number) => {
    extendPageSettings({ page_size: +count });
    PageSettingsService.saveSettings(pageId, { page_size: +count });
  };

  const toggleSorting = (key, val) => {
    PageSettingsService.saveSettings(pageId, {
      order_column_name: key,
      order_direction: val,
    });
    setSorting({ [key]: val });
  };

  const displayFiltersModal = () => {
    ReactModal.open({
      component: 'EnrolledSequencesReportFiltersModal',
      props: {
        initialFilters: filters,
        humanizedTypes: humanizedTypes,
        triggers: triggers,
        categories: categories,
      },
    }).then((newFilters) => {
      setFilters(newFilters);
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Contact',
        id: 'contact',
        sortable: true,
        Cell: ({ row }) => (
          <ContactLink
            id={row.original.contact_id}
            name={row.original.contact_name}
            archived={row.original.contact_archived}
            type={row.original.contact_type}
            status={row.original.contact_status}
            params={{ tab: 'sequences', sequence_id: row.original.id }}
          />
        ),
      },
      {
        Header: FeatureNames.SEQUENCE,
        id: 'sequence',
        sortable: true,
        Cell: ({ row }) => (
          <div>
            {permissions.manage_sequences ? (
              <Link
                route="gym.sequences.edit"
                routeParams={{ sequenceId: row.original.sequence_id }}
              >
                {row.original.sequence_name}
              </Link>
            ) : (
              row.original.sequence_name
            )}
          </div>
        ),
      },
      {
        Header: 'Type',
        id: 'trigger',
        sortable: true,
        accessor: 'humanized_trigger',
      },
      {
        Header: 'Category',
        id: 'category',
        sortable: true,
        Cell: ({ row }) => <div>{row.original.sequence_category_name}</div>,
      },
      {
        Header: 'Status',
        id: 'status',
        sortable: true,
        accessor: 'humanized_status',
      },
      {
        Header: 'Enrolled',
        id: 'enrolled',
        sortable: true,
        Cell: ({ row }) => <div>{dateFormatter(row.original.started_at, 'mediumDate')}</div>,
      },
      {
        Header: 'Last activity',
        id: 'last_activity',
        sortable: true,
        Cell: ({ row }) => (
          <div>{dateFormatter(row.original.last_step_activity_at, 'mediumDate')}</div>
        ),
      },
    ],
    [permissions.manage_sequences],
  );

  const getCsv = () => {
    GTMService.trackPage('Contact Report - Sequence Enrolments', 'Export');
    const params = qs.stringify({
      filters: filters,
    });
    const url = `${sources.enrolledSequences($rootScope.gym.id)}.csv?${params}`;
    $window.open(url, '_blank');
  };

  const updateQuery = () => {
    const query = _.mapObject(filters, (val) => (val === '' ? undefined : val));
    $location.search(query);
  };

  const getEnrolledSequences = (page: number) => {
    if (!pageSettingsLoaded && !sorting) return;
    setLoading(true);
    const url = sources.enrolledSequences($rootScope.gym.id);
    updateQuery();

    api.get(url, { page, count: pageSettings.page_size, sorting, filters }).then((data) => {
      setLoading(false);
      setEnrolledSequences(data);
    });
  };

  return (
    <div className="content-section">
      <header className="page-header page-header--multiple-rows">
        <div className="page-header__tabs">
          <Link className="page-header__item" route="gym.sequences.list">
            {FeatureNames.SEQUENCE_PLURAL}
          </Link>

          {isMasquerading && AdminUserService.has('admin_unarchive_records') && (
            <Link className="page-header__item" route="gym.sequences.archived-sequences">
              <i className="icon-document-delete" /> Archived
            </Link>
          )}

          <button type="button" className="page-header__item page-header__item--active">
            Enrolments
          </button>
        </div>
      </header>

      <div className="worx-site-content">
        <Container noPaddingTop>
          <div className="filters-wrapper filters-wrapper--v2">
            <div className="date-range date-range--v2">
              <DateInput
                value={filters.enrolled_from}
                onChange={(enrolled_from) => extendFilters({ enrolled_from })}
                placeholder="Enrolled after"
                variant="36"
              />
              <DateInput
                value={filters.enrolled_to}
                onChange={(enrolled_to) => extendFilters({ enrolled_to })}
                placeholder="Enrolled before"
                variant="36"
              />
            </div>
          </div>

          <div className="table-header">
            <Input
              value={filters.contact_name}
              onChange={(contact_name) => extendFilters({ contact_name })}
              placeholder="Search by contact name"
            />

            <ToggleTableFiltersButton
              toggle={() => toggleFilters(!filtersOpen)}
              toggleModal={() => displayFiltersModal()}
              active={filtersOpen}
            />
            {AdminUserService.has('admin_perform_csv_exports') && (
              <ExportButton onExport={() => getCsv()} variant="table-header" />
            )}
          </div>

          {filtersOpen && (
            <EnrolledSequencesReportFilters
              initialFilters={filters}
              onChange={setFilters}
              humanizedTypes={humanizedTypes}
              triggers={triggers}
              categories={categories}
            />
          )}

          <Table
            columns={columns}
            data={sequences}
            loading={loading}
            emptyText="No records found"
            pageSize={pageSettings.page_size}
            setPageSize={setPageSize}
            sorting={sorting}
            toggleSorting={toggleSorting}
            loadData={(page) => getEnrolledSequences(page)}
            filters={filters}
            variant="v2"
            withSearchBar
          />
        </Container>
      </div>
    </div>
  );
};

angular.module('gym').value('EnrolledSequencesReport', EnrolledSequencesReport);
